import React, { useState, useRef } from 'react';
import AdUnit from '../components/AdUnit';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';

const JsonComparePage = () => {
  const [leftJson, setLeftJson] = useState('');
  const [rightJson, setRightJson] = useState('');
  const [comparisonOutput, setComparisonOutput] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(true);
  
  // File upload refs
  const leftFileInputRef = useRef(null);
  const rightFileInputRef = useRef(null);
  
  // Refs for scroll syncing
  const leftInputRef = useRef(null);
  const rightInputRef = useRef(null);
  const outputRef = useRef(null);
  const leftLineNumbersRef = useRef(null);
  const rightLineNumbersRef = useRef(null);
  const outputLineNumbersRef = useRef(null);

  // Examples for the comparison
  const examples = {
    simple: {
      left: JSON.stringify({
        "name": "John",
        "age": 30,
        "city": "New York"
      }, null, 2),
      right: JSON.stringify({
        "name": "John",
        "age": 31,
        "country": "USA"
      }, null, 2)
    },
    complex: {
      left: JSON.stringify({
        "id": 1,
        "person": {
          "name": "John",
          "contacts": [
            {"type": "email", "value": "john@example.com"},
            {"type": "phone", "value": "555-1234"}
          ]
        },
        "tags": ["important", "customer"]
      }, null, 2),
      right: JSON.stringify({
        "id": 1,
        "person": {
          "name": "John",
          "contacts": [
            {"type": "email", "value": "john@gmail.com"},
            {"type": "phone", "value": "555-1234"},
            {"type": "address", "value": "123 Main St"}
          ]
        },
        "tags": ["customer", "vip"],
        "status": "active"
      }, null, 2)
    }
  };

  const compareJson = () => {
    try {
      setError('');

      if (!leftJson.trim() || !rightJson.trim()) {
        setError('Please provide both JSON objects to compare.');
        return;
      }

      const leftObject = JSON.parse(leftJson);
      const rightObject = JSON.parse(rightJson);
      
      const comparison = generateComparison(leftObject, rightObject);
      
      // Check if comparison result is null or empty array (no differences)
      if (comparison === null || (Array.isArray(comparison) && comparison.length === 0)) {
        setComparisonOutput(JSON.stringify({
          result: "No differences found. The JSON objects are identical."
        }, null, 2));
      } else {
        setComparisonOutput(JSON.stringify(comparison, null, 2));
      }
    } catch (e) {
      setError('Invalid JSON. Please check your input: ' + e.message);
    }
  };

  // Function to deeply compare and generate a diff object
  const generateComparison = (left, right, path = '') => {
    if (typeof left !== typeof right) {
      return {
        path: path || 'root',
        type: 'type_change',
        left_type: typeof left,
        right_type: typeof right,
        left_value: left,
        right_value: right
      };
    }

    // Handle primitive types (including null)
    if (left === null || right === null || 
        typeof left !== 'object' || 
        typeof right !== 'object') {
      if (left === right) {
        return showOnlyDifferences ? null : {
          path: path || 'root',
          type: 'equal',
          value: left
        };
      } else {
        return {
          path: path || 'root',
          type: 'value_change',
          left_value: left,
          right_value: right
        };
      }
    }

    // Handle arrays
    if (Array.isArray(left) && Array.isArray(right)) {
      const result = [];
      const maxLength = Math.max(left.length, right.length);
      
      for (let i = 0; i < maxLength; i++) {
        const currentPath = `${path}[${i}]`;
        
        if (i >= left.length) {
          result.push({
            path: currentPath,
            type: 'array_item_added',
            right_value: right[i]
          });
        } else if (i >= right.length) {
          result.push({
            path: currentPath,
            type: 'array_item_removed',
            left_value: left[i]
          });
        } else {
          const itemComparison = generateComparison(left[i], right[i], currentPath);
          if (itemComparison) {
            if (Array.isArray(itemComparison)) {
              result.push(...itemComparison);
            } else {
              result.push(itemComparison);
            }
          }
        }
      }
      
      return result.length > 0 ? result : (showOnlyDifferences ? null : {
        path: path || 'root',
        type: 'equal',
        value: left
      });
    }

    // Handle objects
    const allKeys = new Set([...Object.keys(left), ...Object.keys(right)]);
    const result = [];
    
    for (const key of allKeys) {
      const currentPath = path ? `${path}.${key}` : key;
      
      if (!(key in left)) {
        result.push({
          path: currentPath,
          type: 'property_added',
          right_value: right[key]
        });
      } else if (!(key in right)) {
        result.push({
          path: currentPath,
          type: 'property_removed',
          left_value: left[key]
        });
      } else {
        const valueComparison = generateComparison(left[key], right[key], currentPath);
        if (valueComparison) {
          if (Array.isArray(valueComparison)) {
            result.push(...valueComparison);
          } else {
            result.push(valueComparison);
          }
        }
      }
    }
    
    return result.length > 0 ? result : (showOnlyDifferences ? null : {
      path: path || 'root',
      type: 'equal',
      value: left
    });
  };

  const handleLeftFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          // Validate JSON
          JSON.parse(content);
          setLeftJson(content);
          setError('');
        } catch (err) {
          setError('Invalid JSON file on the left side.');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleRightFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          // Validate JSON
          JSON.parse(content);
          setRightJson(content);
          setError('');
        } catch (err) {
          setError('Invalid JSON file on the right side.');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleLeftUploadClick = () => {
    if (leftFileInputRef.current) {
      leftFileInputRef.current.click();
    }
  };

  const handleRightUploadClick = () => {
    if (rightFileInputRef.current) {
      rightFileInputRef.current.click();
    }
  };

  const loadExample = (exampleKey) => {
    if (examples[exampleKey]) {
      setLeftJson(examples[exampleKey].left);
      setRightJson(examples[exampleKey].right);
      setComparisonOutput('');
      setError('');
    }
  };

  const handleLeftInputScroll = () => {
    if (leftInputRef.current && leftLineNumbersRef.current) {
      leftLineNumbersRef.current.scrollTop = leftInputRef.current.scrollTop;
    }
  };

  const handleRightInputScroll = () => {
    if (rightInputRef.current && rightLineNumbersRef.current) {
      rightLineNumbersRef.current.scrollTop = rightInputRef.current.scrollTop;
    }
  };

  const handleOutputScroll = () => {
    if (outputRef.current && outputLineNumbersRef.current) {
      outputLineNumbersRef.current.scrollTop = outputRef.current.scrollTop;
    }
  };

  const handleCopyOutput = () => {
    if (!comparisonOutput) return;
    navigator.clipboard.writeText(comparisonOutput).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    });
  };

  const clearAll = () => {
    setLeftJson('');
    setRightJson('');
    setComparisonOutput('');
    setError('');
  };

  const prettyPrintBoth = () => {
    try {
      if (leftJson.trim()) {
        const leftObj = JSON.parse(leftJson);
        setLeftJson(JSON.stringify(leftObj, null, 2));
      }
      
      if (rightJson.trim()) {
        const rightObj = JSON.parse(rightJson);
        setRightJson(JSON.stringify(rightObj, null, 2));
      }
      
      setError('');
    } catch (e) {
      setError('Error formatting JSON: ' + e.message);
    }
  };

  // Function to render inline JSON differences
  const renderInlineJsonDiff = () => {
    if (!comparisonOutput) return null;
    
    try {
      const comparison = JSON.parse(comparisonOutput);
      
      // If no differences message
      if (comparison.result && comparison.result.includes("No differences")) {
        return (
          <div className="inline-diff-container">
            <div className="diff-side">
              <h4>Base JSON</h4>
              <pre className="json-display no-differences">{leftJson}</pre>
            </div>
            <div className="diff-side">
              <h4>Comparison Result</h4>
              <pre className="json-display no-differences">{rightJson}</pre>
            </div>
          </div>
        );
      }
      
      // Create diff maps for easy lookup
      const diffMap = new Map();
      if (Array.isArray(comparison)) {
        comparison.forEach(diff => {
          diffMap.set(diff.path, diff);
        });
      }
      
      return (
        <div className="inline-diff-container">
          <div className="diff-side">
            <h4>Base JSON</h4>
            <pre className="json-display">{leftJson}</pre>
          </div>
          <div className="diff-side">
            <h4>Comparison Result</h4>
            <pre className="json-display">{renderJsonWithDiff(rightJson, diffMap, 'right')}</pre>
          </div>
        </div>
      );
    } catch (e) {
      return (
        <div className="inline-diff-container">
          <div className="diff-side">
            <h4>Base JSON</h4>
            <pre className="json-display">{leftJson}</pre>
          </div>
          <div className="diff-side">
            <h4>Comparison Result</h4>
            <pre className="json-display">{rightJson}</pre>
          </div>
        </div>
      );
    }
  };
  
  // Function to render JSON with diff highlighting
  const renderJsonWithDiff = (jsonString, diffMap, side) => {
    if (!jsonString || !diffMap) return jsonString;
    
    try {
      const leftObj = JSON.parse(leftJson);
      const rightObj = JSON.parse(rightJson);
      
      // Build a structure that includes removed properties
      const displayObj = buildDisplayObject(leftObj, rightObj, diffMap);
      const displayJson = JSON.stringify(displayObj, null, 2);
      const lines = displayJson.split('\n');
      
      // Apply highlighting to lines based on markers
      // Create line-by-line diff analysis
      const lineAnalysis = analyzeLines(lines, diffMap, leftObj, rightObj);
      
      // Apply highlighting to lines
      const highlightedLines = lines.map((line, index) => {
        const analysis = lineAnalysis[index];
        
        if (analysis && analysis.type) {
          let indicator = '';
          switch (analysis.type) {
            case 'added': indicator = '+ '; break;
            case 'removed': indicator = '- '; break;
            case 'changed': indicator = '~ '; break;
          }
          
          return (
            <span key={index} className={`line-diff line-${analysis.type}`}>
              <span className="diff-indicator">{indicator}</span>
              {line}
            </span>
          );
        }
        
        return <span key={index} className="line-normal">{line}</span>;
      });
      
      return highlightedLines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < highlightedLines.length - 1 && '\n'}
        </React.Fragment>
      ));
      
    } catch (e) {
      return jsonString;
    }
  };
  
  // Build display object that includes removed properties
  const buildDisplayObject = (leftObj, rightObj, diffMap) => {
    const result = JSON.parse(JSON.stringify(rightObj));
    
    // Add removed properties back to the display
    diffMap.forEach((diff, path) => {
      if (diff.type === 'property_removed' || diff.type === 'array_item_removed') {
        setValueAtPath(result, path, diff.left_value);
      }
    });
    
    return result;
  };
  
  // Analyze each line to determine its diff status
  const analyzeLines = (lines, diffMap, leftObj, rightObj) => {
    const analysis = {};
    
    diffMap.forEach((diff, path) => {
      const pathParts = path.split('.');
      const lastKey = pathParts[pathParts.length - 1].replace(/\[\d+\]/, ''); // Remove array indices
      
      // Find lines that contain this property
      lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        
        // Check if this line contains the property key
        if (trimmedLine.includes(`"${lastKey}":`)) {
          if (diff.type === 'property_added' || diff.type === 'array_item_added') {
            analysis[index] = { type: 'added', path };
          } else if (diff.type === 'property_removed' || diff.type === 'array_item_removed') {
            analysis[index] = { type: 'removed', path };
          } else if (diff.type === 'value_change' || diff.type === 'type_change') {
            analysis[index] = { type: 'changed', path };
          }
        }
      });
    });
    
    return analysis;
  };
  
  // Helper function to set value at a specific path
  const setValueAtPath = (obj, path, value) => {
    const parts = path.split('.');
    let current = obj;
    
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      
      if (part.includes('[') && part.includes(']')) {
        const [key, indexStr] = part.split('[');
        const index = parseInt(indexStr.replace(']', ''));
        
        if (key) {
          if (!current[key]) current[key] = [];
          current = current[key];
        }
        
        if (!current[index]) current[index] = {};
        current = current[index];
      } else {
        if (!current[part]) current[part] = {};
        current = current[part];
      }
    }
    
    const lastPart = parts[parts.length - 1];
    if (lastPart.includes('[') && lastPart.includes(']')) {
      const [key, indexStr] = lastPart.split('[');
      const index = parseInt(indexStr.replace(']', ''));
      
      if (key) {
        if (!current[key]) current[key] = [];
        current = current[key];
      }
      
      current[index] = value;
    } else {
      current[lastPart] = value;
    }
  };
  
  // Helper function to get value at a specific path
  const getValueAtPath = (obj, path) => {
    const parts = path.split('.');
    let current = obj;
    
    for (const part of parts) {
      if (part.includes('[') && part.includes(']')) {
        const [key, indexStr] = part.split('[');
        const index = parseInt(indexStr.replace(']', ''));
        if (key) current = current[key];
        if (Array.isArray(current) && !isNaN(index)) {
          current = current[index];
        } else {
          return undefined;
        }
      } else {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          return undefined;
        }
      }
    }
    
    return current;
  };

  return (
    <>
      <SEO 
        title="JSON Compare Tool - Find Differences Between JSON Objects" 
        description="Compare two JSON objects and see the differences between them. Identify added, removed, and changed properties with this easy-to-use JSON diff tool."
        keywords={["JSON compare", "JSON diff", "JSON difference", "compare JSON", "JSON comparison tool", "find JSON differences", "JSON objects comparison"]}
        canonicalUrl="https://yourjsontools.com/json-compare"
      />
      
      <div className="json-compare-container">
        <h1>JSON Compare Tool</h1>
        
        <div className="inputs-container">
          {/* Left JSON input */}
          <div className="json-input-container">
            <div className="input-header">
              <h3>Left JSON</h3>
              <div>
                <input type="file" ref={leftFileInputRef} style={{ display: 'none' }} onChange={handleLeftFileUpload} accept=".json,.txt" />
                <button onClick={handleLeftUploadClick} className="control-button upload-button">Upload</button>
              </div>
            </div>
            <TextareaWithLines
              value={leftJson}
              onChange={e => setLeftJson(e.target.value)}
              placeholder={"Enter your first JSON object here..."}
              style={{ height: '400px' }}
              textareaRef={leftInputRef}
              lineNumbersRef={leftLineNumbersRef}
              onScroll={handleLeftInputScroll}
            />
          </div>
          
          {/* Right JSON input */}
          <div className="json-input-container">
            <div className="input-header">
              <h3>Right JSON</h3>
              <div>
                <input type="file" ref={rightFileInputRef} style={{ display: 'none' }} onChange={handleRightFileUpload} accept=".json,.txt" />
                <button onClick={handleRightUploadClick} className="control-button upload-button">Upload</button>
              </div>
            </div>
            <TextareaWithLines
              value={rightJson}
              onChange={e => setRightJson(e.target.value)}
              placeholder={"Enter your second JSON object here..."}
              style={{ height: '400px' }}
              textareaRef={rightInputRef}
              lineNumbersRef={rightLineNumbersRef}
              onScroll={handleRightInputScroll}
            />
          </div>
        </div>
        
        {/* Controls */}
        <div className="controls-container">
          <button onClick={compareJson} className="control-button" style={{ backgroundColor: '#007bff', fontWeight: 'bold' }}>Compare JSON</button>
          <button onClick={prettyPrintBoth} className="control-button" style={{ backgroundColor: '#28a745' }}>Format Both</button>
          <button onClick={clearAll} className="control-button" style={{ backgroundColor: '#6c757d' }}>Clear All</button>
          <select onChange={(e) => loadExample(e.target.value)} className="control-select" defaultValue="">
            <option value="" disabled>Load Example</option>
            <option value="simple">Simple Example</option>
            <option value="complex">Complex Example</option>
          </select>
          <div className="checkbox-container">
            <input 
              type="checkbox" 
              id="showOnlyDifferences" 
              checked={showOnlyDifferences} 
              onChange={() => setShowOnlyDifferences(!showOnlyDifferences)} 
              defaultChecked={true}
            />
            <label htmlFor="showOnlyDifferences">Show Only Differences</label>
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}
        
        {/* Comparison Output */}
        {comparisonOutput && (
          <div className="output-container">
            <div className="output-header">
              <h3>Comparison Result</h3>
              <button onClick={handleCopyOutput} className="copy-button">
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            </div>
            {renderInlineJsonDiff()}
          </div>
        )}
      </div>
      
      <style jsx global>{`
        .json-compare-container {
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: %95;
          margin: 0 auto;
          padding: 20px;
          gap: 20px;
          box-sizing: border-box;
        }
        
        .json-compare-container h1 {
          text-align: center;
          margin: 0 0 10px 0;
        }
        
        .inputs-container {
          display: flex;
          gap: 20px;
          width: 100%;
        }
        
        .json-input-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 10px;
          width: 100%;
        }
        
        .input-header, .output-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }
        
        .input-header h3, .output-header h3 {
          margin: 0;
        }
        
        .upload-button {
          background-color: #00BFA5;
          padding: 6px 12px;
          margin-left: 10px;
          font-size: 12px;
        }
        
        .json-compare-container .line-numbered-textarea,
        .json-compare-container .line-numbered-pre {
          width: 100% !important;
        }
        
        .json-compare-container textarea,
        .json-compare-container pre {
          width: 100% !important;
          box-sizing: border-box !important;
          background-color: #fff;
        }
        
        .controls-container {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 10px;
        }
        
        .checkbox-container {
          display: flex;
          align-items: center;
          background-color: #f8f9fa;
          padding: 0 10px;
          border-radius: 4px;
          height: 38px;
        }
        
        .checkbox-container input {
          margin-right: 5px;
        }
        
        .checkbox-container label {
          font-size: 14px;
          cursor: pointer;
        }
        
        .error-message {
          padding: 10px;
          color: white;
          background-color: #dc3545;
          text-align: left;
          font-weight: normal;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .output-container {
          margin-top: 20px;
          position: relative;
          width: 100%;
        }
        
        .output-header {
          margin-bottom: 10px;
        }
        
        .control-button {
          padding: 8px 16px;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s ease, opacity 0.2s ease;
        }
        
        .control-button:hover {
          opacity: 0.85;
        }
        
        .control-select {
          padding: 8px 16px;
          border: 1px solid #ccc;
          border-radius: 4px;
        }
        
        /* Inline Diff Styles */
        .inline-diff-container {
          display: flex;
          gap: 20px;
          width: 100%;
          margin-top: 10px;
        }
        
        .diff-side {
          flex: 1;
          min-width: 0;
        }
        
        .diff-side h4 {
          margin: 0 0 10px 0;
          padding: 10px;
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-bottom: none;
          border-radius: 4px 4px 0 0;
          font-size: 14px;
          font-weight: 600;
        }
        
        .json-display {
          margin: 0;
          padding: 15px;
          background-color: #fff;
          border: 1px solid #dee2e6;
          border-radius: 0 0 4px 4px;
          font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
          font-size: 13px;
          line-height: 1.5;
          max-height: 500px;
          overflow: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }
        
        .json-display.no-differences {
          border-color: #28a745;
          background-color: #f8fff8;
        }
        
        .line-diff {
          display: inline;
          position: relative;
          padding: 2px 4px;
          margin: 0 -4px;
          border-radius: 3px;
        }
        
        .line-added {
          background-color: #e6ffed;
          border-left: 3px solid #28a745;
          padding-left: 8px;
        }
        
        .line-removed {
          background-color: #ffeef0;
          border-left: 3px solid #d73a49;
          padding-left: 8px;
        }
        
        .line-changed {
          background-color: #fff3cd;
          border-left: 3px solid #ffc107;
          padding-left: 8px;
        }
        
        .line-normal {
          display: inline;
        }
        
        .diff-indicator {
          font-weight: bold;
          margin-right: 8px;
          display: inline-block;
          width: 20px;
          font-family: monospace;
        }
        
        .line-added .diff-indicator {
          color: #28a745;
        }
        
        .line-removed .diff-indicator {
          color: #d73a49;
        }
        
        .line-changed .diff-indicator {
          color: #ffc107;
        }
        
        .copy-button {
          background-color: #0366d6;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 5px 10px;
          cursor: pointer;
          font-size: 12px;
          transition: background-color 0.2s;
        }
        
        .copy-button:hover {
          background-color: #0056b3;
        }

        @media (max-width: 768px) {
          .inputs-container {
            flex-direction: column;
          }
          
          .inline-diff-container {
            flex-direction: column;
            gap: 20px;
          }
          
          .diff-side {
            width: 100%;
          }
          
          .json-display {
            font-size: 12px;
            max-height: 300px;
          }
        }
      `}</style>

      <div style={{ maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>About JSON Compare Tool</h2>
        <p style={{ marginBottom: '15px' }}>
          The JSON Compare tool helps you identify differences between two JSON objects. Whether you're troubleshooting API responses, comparing configurations, or tracking changes in data, this tool makes it easy to spot what's different.
        </p>
        
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>How It Works:</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>Enter or upload two JSON objects in the left and right panels.</li>
          <li style={{ marginBottom: '8px' }}>Click "Compare JSON" to analyze the differences.</li>
          <li style={{ marginBottom: '8px' }}>The tool displays a detailed report of all differences, including:</li>
          <ul style={{ paddingLeft: '20px', marginTop: '5px' }}>
            <li style={{ marginBottom: '4px' }}><strong>Type Changes:</strong> When a property exists in both objects but has different types.</li>
            <li style={{ marginBottom: '4px' }}><strong>Value Changes:</strong> When a property exists in both objects but has different values.</li>
            <li style={{ marginBottom: '4px' }}><strong>Added Properties:</strong> Properties that exist in the right object but not in the left.</li>
            <li style={{ marginBottom: '4px' }}><strong>Removed Properties:</strong> Properties that exist in the left object but not in the right.</li>
            <li style={{ marginBottom: '4px' }}><strong>Array Differences:</strong> Added, removed, or changed items in arrays.</li>
          </ul>
        </ul>
        
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Use Cases:</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>Comparing API responses in different environments.</li>
          <li style={{ marginBottom: '8px' }}>Verifying changes to configuration files.</li>
          <li style={{ marginBottom: '8px' }}>Debugging differences between expected and actual data.</li>
          <li style={{ marginBottom: '8px' }}>Reviewing version differences in JSON documents.</li>
        </ul>
        
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Features:</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Deep Object Comparison:</strong> Analyzes nested objects and arrays.</li>
          <li style={{ marginBottom: '8px' }}><strong>Path Reporting:</strong> Shows exact paths to each difference.</li>
          <li style={{ marginBottom: '8px' }}><strong>File Upload:</strong> Import JSON directly from files.</li>
          <li style={{ marginBottom: '8px' }}><strong>Format Option:</strong> Pretty-print JSON for better readability.</li>
          <li style={{ marginBottom: '8px' }}><strong>Filter Option:</strong> Toggle to show only differences or all properties.</li>
        </ul>
        
        <p style={{ marginBottom: '15px' }}>
          This tool processes all data locally in your browser, ensuring your JSON data remains private and secure. No data is sent to any server.
        </p>
      </div>
      
    </>
  );
};

export default JsonComparePage; 